import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { JavaSeo } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'

const CourseCard = dynamic(() =>
  import('@components/course-landing-page/course-card')
)

const { JAVA } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['apcsa'].trialCourseQuery}`

const Java = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  useEffect(() => {
    const handleScroll = () => setShouldUpdateCourseCard(window.scrollY > 600)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...JavaSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[JAVA].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Top Coding Class Instructors for Kids and Teens to Learn Java"
        description="Embark on an exciting journey into the world of Java with Codingal, where kids and teens learn from top coding instructors through a structured, engaging curriculum designed to build a strong foundation in programming. Starting with core concepts like data types, operators, and control structures, your child will gradually progress to mastering loops, arrays, functions, and even advanced topics like recursion and algorithmic thinking. Every lesson is carefully crafted to boost their confidence, spark creativity, and enhance problem-solving skills through interactive coding challenges and real-world projects. What your child will learn includes mastering core programming concepts, developing essential problem-solving tools, and applying advanced logic techniques—all while having fun and staying engaged. Whether your child is just starting out or looking to take their skills to the next level, learning Java at Codingal sets them up for success not just in tech, but in any future career that values analytical thinking and creativity."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        shouldHideCourseCard={shouldUpdateCourseCard}
        courseName={JAVA}
        urlQuery={urlQuery}
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={JAVA} />
          <RecommendedCourses
            courseName={JAVA}
            courseCodes={['apcsa', 'cgm']}
            title="Our Java class curriculum"
            description={
              <Trans>
                At Codingal, we focus on teaching what truly matters—the key
                concepts that empower you to excel in the world of Java
                programming. You’ll begin by building a strong foundation,
                understanding Java’s essential building blocks like data types,
                operators, and control structures. These core elements form the
                backbone of your programming journey. By mastering them early
                on, you’ll be well-prepared to tackle more advanced challenges
                as you progress.
                <br />
                Next, you’ll delve into the heart of Java—object-oriented
                programming (OOP). You’ll learn how concepts like inheritance
                and polymorphism enable you to write cleaner, more efficient
                code. Hands-on experience with crucial algorithms and data
                structures, such as arrays and ArrayLists, will equip you to
                solve real-world problems effectively. Each lesson focuses on
                practical application, helping you bring your ideas to
                life—whether it’s building a game, creating an app, or solving
                complex challenges.
                <br />
                By the end, you won’t just know how to code—you’ll understand
                what it takes to become a skilled, confident programmer ready to
                tackle the future.
              </Trans>
            }
          />
          <Activities courseName={JAVA} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked coding tutors
                ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                All our instructors have a background in Computer Science and
                undergo rigorous training, contributing to our outstanding NPS
                score of 86. Teachers receive a rating of 4.9 out of 5 from
                students and parents, while the curriculum content is rated 4.8
                out of 5. With a teacher ratio of 91% female to 9% male, we
                provide a comfortable learning environment. Our tutors
                understand students&#39; needs and offer personalized attention,
                even in group classes. Additionally, students have access to
                24/7 doubt resolution with an expert tutor whenever they need
                assistance.
              </Trans>
            }
          />
          <CourseFeatures
            courseTitle={JAVA}
            title={<Trans>An engaging Java programming class for kids</Trans>}
          />

          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we create an ideal environment for kids to master
                  Java from the ground up. Our thoughtfully designed curriculum
                  combines real-world tools, creative projects, and interactive
                  learning to ensure a comprehensive and enjoyable learning
                  experience.
                </span>
              </Trans>
            }
            courseName={JAVA}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                At Codingal, we take pride in being a STEM.org-accredited coding
                school, employing scientific teaching methods based on
                Bloom&#39;s Taxonomy, BIDE, and STEAM principles. Our Java
                curriculum is meticulously aligned with Common Core, state
                standards, and Advanced Placement (AP) curricula, ensuring
                students receive a thorough and relevant education in
                programming. We also incorporate the K-12 Computer Science
                Framework, delivering developmentally appropriate content that
                prepares learners for success in today’s technology-driven
                world.
                <br />
                For example, when students create applications like a
                temperature converter or a scientific calculator in Java, they
                combine coding skills with mathematical and scientific concepts.
                Similarly, when they learn & implement algorithms to solve
                real-world problems, they engage with engineering and logical
                reasoning principles.
                <br />
                STEM.org, a US-based research and credentialing organization,
                ensures quality assurance for educational programs by meeting
                the highest standards in STEM education. STEM represents
                Science, Technology, Engineering, and Mathematics, all
                seamlessly integrated into Codingal’s Java curriculum.
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Java certifications"
            description={
              <>
                The Codingal Grandmaster course is specially designed for
                students in grades 6 to 12, offering a comprehensive learning
                path through two dedicated modules: Java and Advanced Java. This
                course provides your child with the tools they need to develop a
                strong foundation in programming, explore advanced concepts, and
                create real-world projects that can be proudly shared.
                <br />
                Upon completing the course, students will earn the
                STEM.org-certified Coding Grandmaster certificate, a prestigious
                recognition of their Java expertise. This certification not only
                marks your child’s achievement but also adds a valuable
                credential to their academic portfolio.
                <br />
                The Coding Grandmaster certificate is STEM.org-accredited, easy
                to download and share, and serves as an impressive
                accomplishment that your child can showcase to peers, teachers,
                and future academic or career opportunities. It’s a great way to
                encourage your child’s growth while giving them an edge in their
                future endeavors, all within a fun and engaging coding
                experience.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview shouldScrollToTop isCoursepage />
          <CodingForStudents
            title="Our Java classes are appropriate for middle school & High school students"
            description={
              <>
                At Codingal, our Java classes are designed specifically for
                Middle & High school students, with a clear and easy-to-follow
                course progression. We start with fun, beginner-level concepts
                that help students understand the basics of coding in a simple
                and engaging way. As they grow more confident, they progress to
                more advanced topics, gradually building a strong foundation in
                coding. This structured approach ensures that students develop
                the skills they need to tackle more complex projects and
                programming languages as they continue their coding journey.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <Faq courseName={JAVA} title="Frequently Asked Questions" />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['cgm', 'apcsa', 'aigc']} />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={JAVA}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Java for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Java
